import { Fragment } from "react";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { Tooltip } from "react-tooltip";

import { Icon } from "~/assets/icons/Icon";
import Select from "~/components/mantine/select/Select";
import AddItemButton from "~/components/ui/addItemButton/AddItemButton";
import Input from "~/components/ui/input/Input";
import Modal from "~/components/ui/modal/Modal";

import styles from "./AdvancedConditionsModal.module.scss";

export type ConditionField = "path" | "query";
export type ConditionOperator =
  | "equals"
  | "not_equals"
  | "prefix"
  | "not_prefix"
  | "suffix"
  | "not_suffix"
  | "contains"
  | "not_contains";

export type ConditionFormValues = {
  field: ConditionField;
  operator: ConditionOperator;
  value: string;
};

export type ConditionGroup = { conditions: ConditionFormValues[] };

type FormValues = {
  groups: ConditionGroup[];
};

const FIELD_OPTIONS: { value: ConditionField; label: string }[] = [
  { value: "path", label: "Path" },
  { value: "query", label: "Query" },
];

const OPERATOR_OPTIONS: { value: ConditionOperator; label: string }[] = [
  { value: "equals", label: "Equals" },
  { value: "prefix", label: "Starts with" },
  { value: "suffix", label: "Ends with" },
  { value: "contains", label: "Contains" },
  { value: "not_equals", label: "Not equals" },
  { value: "not_prefix", label: "Not starts with" },
  { value: "not_suffix", label: "Not ends with" },
  { value: "not_contains", label: "Not contains" },
];

const emptyCondition = (): ConditionFormValues => ({
  field: "path",
  operator: "contains",
  value: "",
});

const FORM_ID = "advanced-conditions-form";

// Mirrors JSON Schema constraints on
// site_customization_action_attributes.conditions (src/api/schema.yaml).
const MAX_GROUPS = 5; // g.maxProperties
const MAX_VALUE_LENGTH = 2048; // path_*/query_* maxLength

type AdvancedConditionsModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onApply: (groups: ConditionGroup[]) => void;
  initialGroups?: ConditionGroup[];
};

export const AdvancedConditionsModal = ({
  isOpen,
  onClose,
  onApply,
  initialGroups,
}: AdvancedConditionsModalProps) => {
  const hasConditions =
    initialGroups &&
    initialGroups.length > 0 &&
    initialGroups.some((g) =>
      g.conditions.some((c) => c.value.trim().length > 0),
    );

  return (
    <Modal
      isOpen={isOpen}
      closeModal={onClose}
      modalLabel={<span className={styles.modalTitle}>Advanced Search</span>}
      buttonLabel="Apply"
      formId={FORM_ID}
      size="xl"
      footerLeft={
        hasConditions ? (
          <button
            type="button"
            className={styles.clearBtn}
            onClick={() => {
              onApply([]);
              onClose();
            }}
          >
            Clear All
          </button>
        ) : undefined
      }
    >
      {isOpen && (
        <AdvancedConditionsContent
          onClose={onClose}
          onApply={onApply}
          initialGroups={initialGroups}
        />
      )}
    </Modal>
  );
};

const AdvancedConditionsContent = ({
  onClose,
  onApply,
  initialGroups,
}: Omit<AdvancedConditionsModalProps, "isOpen">) => {
  const { control, handleSubmit } = useForm<FormValues>({
    defaultValues: {
      groups:
        initialGroups && initialGroups.length > 0
          ? initialGroups
          : [{ conditions: [emptyCondition()] }],
    },
  });

  const {
    fields: groupFields,
    append: appendGroup,
    remove: removeGroup,
  } = useFieldArray({ control, name: "groups" });

  const onSubmit = (data: FormValues) => {
    // Filter out empty conditions
    const nonEmptyGroups = data.groups
      .map((g) => ({
        conditions: g.conditions.filter((c) => c.value.trim().length > 0),
      }))
      .filter((g) => g.conditions.length > 0);

    onApply(nonEmptyGroups);
    onClose();
  };

  return (
    <form id={FORM_ID} onSubmit={handleSubmit(onSubmit)}>
      <div className={styles.inner}>
        <div>
          <div className={styles.groups}>
            {groupFields.map((groupField, gi) => (
              <Fragment key={groupField.id}>
                <GroupRow
                  groupIndex={gi}
                  control={control}
                  canRemove={groupFields.length > 1}
                  onRemove={() => removeGroup(gi)}
                />
                {gi < groupFields.length - 1 && (
                  <div className={styles.orDivider}>OR</div>
                )}
              </Fragment>
            ))}

            {groupFields.length < MAX_GROUPS && (
              <AddItemButton
                onClick={() => appendGroup({ conditions: [emptyCondition()] })}
              >
                + OR
              </AddItemButton>
            )}
          </div>
        </div>
      </div>
      <Tooltip
        id="adv-cond-error-tooltip"
        style={{
          borderRadius: "var(--border-radius)",
          font: "var(--ui-text-12-medium)",
        }}
      />
    </form>
  );
};

// ── Group sub-component ────────────────────────────────────────

type GroupRowProps = {
  groupIndex: number;
  control: ReturnType<typeof useForm<FormValues>>["control"];
  canRemove: boolean;
  onRemove: () => void;
};

const GroupRow = ({
  groupIndex,
  control,
  canRemove,
  onRemove,
}: GroupRowProps) => {
  const { fields, append, remove } = useFieldArray({
    control,
    name: `groups.${groupIndex}.conditions`,
  });

  return (
    <div className={styles.group}>
      {canRemove && (
        <div className={styles.groupHeader}>
          <button
            type="button"
            className={styles.removeBtn}
            onClick={onRemove}
            title="Remove group"
          >
            <Icon iconName="TrashIcon" width="0.875rem" height="0.875rem" />
          </button>
        </div>
      )}

      <div className={styles.groupConditions}>
        {fields.map((condField, ci) => (
          <div key={condField.id}>
            <div className={styles.conditionRow}>
              <Controller
                name={`groups.${groupIndex}.conditions.${ci}.field`}
                control={control}
                render={({ field }) => (
                  <Select
                    data={FIELD_OPTIONS}
                    value={field.value}
                    onChange={(v) => v && field.onChange(v)}
                    searchable={false}
                  />
                )}
              />
              <Controller
                name={`groups.${groupIndex}.conditions.${ci}.operator`}
                control={control}
                render={({ field }) => (
                  <Select
                    data={OPERATOR_OPTIONS}
                    value={field.value}
                    onChange={(v) => v && field.onChange(v)}
                    searchable={false}
                  />
                )}
              />
              <Controller
                name={`groups.${groupIndex}.conditions.${ci}.value`}
                control={control}
                rules={{
                  validate: (v) => v.trim().length > 0 || "Required",
                }}
                render={({ field, fieldState }) => (
                  <div className={styles.inputWithError}>
                    <Input
                      value={field.value}
                      onChange={(e) => {
                        const v = (e.target as HTMLInputElement).value;
                        field.onChange(v.slice(0, MAX_VALUE_LENGTH));
                      }}
                      placeholder="/blog/*"
                      errormessage={fieldState.error?.message}
                      ownProps={{ noErrors: true }}
                    />
                    {fieldState.error && (
                      <span
                        className={styles.errorIcon}
                        data-tooltip-id="adv-cond-error-tooltip"
                        data-tooltip-content={fieldState.error.message}
                      >
                        ⚠
                      </span>
                    )}
                  </div>
                )}
              />
              {fields.length > 1 && (
                <button
                  type="button"
                  className={styles.removeBtn}
                  onClick={() => remove(ci)}
                  title="Remove condition"
                >
                  <Icon
                    iconName="TrashIcon"
                    width="0.875rem"
                    height="0.875rem"
                  />
                </button>
              )}
            </div>
          </div>
        ))}

        <div className={styles.addConditionRow}>
          <AddItemButton onClick={() => append(emptyCondition())}>
            + AND
          </AddItemButton>
        </div>
      </div>
    </div>
  );
};
